import { useLocation, useNavigate } from "react-router-dom";
import { previousVisit } from "../lib/backtrail";

/**
 * The default "Back to <previous page>" link at the head of a detail page's
 * trail (Jonathan, 2026-10-25: this replaces the big arrow that was in the main
 * header). It pops real history (skipping same-page filter changes) and NAMES where that goes, so
 * an account opened out of the cash floors reads "← Back to Cash floors". The
 * page's own contextual links (All accounts, the entity, Dashboard) follow it.
 *
 * Hidden on the first page of a visit — a deep link or a fresh tab has no
 * previous page, so there is nothing to go back to and the contextual links
 * stand alone.
 *
 * `standalone` renders a return link in its own breadcrumb. PageHeader uses
 * it for ordinary pages, including dashboard drills and Manual Balances.
 * Detail pages keep this control inside their existing contextual trail and
 * disable the shared header copy. A fresh deep link has no fabricated source.
 */
export default function PageBack({ standalone = false }: { standalone?: boolean } = {}) {
  const navigate = useNavigate();
  // Subscribe to navigation so the previous entry is recomputed each move.
  useLocation();
  const prev = previousVisit();
  if (!prev) return null;
  // The `back` class supplies the ← arrow (::before) and the weight, like the
  // other trail links; crumb-back only strips the <button> chrome.
  const link = (
    <button
      type="button"
      className="back crumb-back"
      onClick={() => navigate(-prev.delta)}
    >
      Back to {prev.label}
    </button>
  );
  // On a list page the back link stands on its own; on a detail page it leads
  // into the page's own contextual links, so it keeps the trailing separator.
  if (standalone) return <div className="breadcrumb">{link}</div>;
  return (
    <>
      {link}
      {" "}·{" "}
    </>
  );
}
